import { useTranslation } from 'react-i18next';
import { ColumnDef } from '@tanstack/react-table';
import { linkOptions } from '@tanstack/react-router';
import { capitalizeText } from '@anytag/shared-utils';
import { SpreadButton } from '@anytag/shared-molecules';
import { useTranslateCountry } from '@anytag/shared-hooks';
import { AnalyticsAuthSocialAccountType } from '@anytag/graphql';
import { CustomSocialIcon, TableCell } from '@anytag/shared-atoms';
import { ANALYTICS_SUBSCRIPTION_TRANSLATION_KEYS } from '@anytag/shared-constants';
import { useUserRoles, useAnalyticsPermissions, useAdvertiserPermissions } from '@anytag/auth';
import { AnalyticsSupportedSocialMediaType, ReconnectAnalyticsAccountType } from '@anytag/shared-types';
import { AnalyticsAccountType } from '../types';
import { AnalyticsProfile } from '../AnalyticsProfile';

interface Props {
  onDelete: (account: { id: number; name: string }) => void;
  onReconnectAccount: (account: ReconnectAnalyticsAccountType) => void;
}

export const useAnalyticsListTable = ({ onDelete, onReconnectAccount }: Props) => {
  const { t } = useTranslation();
  const { tc } = useTranslateCountry();
  const { hideEditAdvertiserBtn } = useAdvertiserPermissions();
  const { isAdminStaff, isAdminStaffAgencyTalentAgencyPartner } = useUserRoles();
  const { hideEditAnalyticsBtn, hideDeleteAnalyticsBtn } = useAnalyticsPermissions();

  const formatAccounts = ({
    twitter,
    youtube,
    instagram,
    facebookPage
  }: AnalyticsAccountType['socialMediaAccounts']): ReconnectAnalyticsAccountType[] => [
    {
      id: instagram?.id,
      needReconnect: instagram?.needReconnect,
      socialAccountId: instagram?.businessAccountId,
      socialAccountName: instagram?.instagramAccountName,
      socialMedia: AnalyticsSupportedSocialMediaType.instagram,
      socialAccountType: AnalyticsAuthSocialAccountType.INSTAGRAM,
      socialMediaUrl: `https://instagram.com/${instagram?.instagramAccountName}`,
      linkedAccount: instagram?.fbPageId
        ? {
            socialAccountId: instagram.fbPageId,
            socialAccountName: instagram.fbPageName,
            socialAccountType: AnalyticsAuthSocialAccountType.FACEBOOK_PAGE
          }
        : undefined
    },
    {
      id: youtube?.id,
      socialAccountId: youtube?.channelId,
      needReconnect: youtube?.needReconnect,
      socialAccountName: youtube?.channelName,
      socialMedia: AnalyticsSupportedSocialMediaType.youtube,
      socialAccountType: AnalyticsAuthSocialAccountType.YOUTUBE,
      socialMediaUrl: `https://youtube.com/channel/${youtube?.channelId}/`
    },
    {
      id: facebookPage?.id,
      socialAccountId: facebookPage?.fbPageId,
      needReconnect: facebookPage?.needReconnect,
      socialAccountName: facebookPage?.fbPageName,
      socialMediaUrl: facebookPage?.pageUrl || '',
      socialMedia: AnalyticsSupportedSocialMediaType.facebook,
      socialAccountType: AnalyticsAuthSocialAccountType.FACEBOOK_PAGE
    },
    {
      id: twitter?.id,
      socialAccountName: twitter?.name,
      needReconnect: twitter?.needReconnect,
      socialAccountId: twitter?.twitterUserId,
      socialMedia: AnalyticsSupportedSocialMediaType.twitter,
      socialAccountType: AnalyticsAuthSocialAccountType.TWITTER,
      socialMediaUrl: `https://twitter.com/${twitter?.username}`
    }
  ];

  const getAnalyticsDetailsLink = (brandAccountId: number) =>
    linkOptions({
      to: '/analytics/$brandAccountId',
      params: { brandAccountId: brandAccountId.toString() }
    });

  const hideEditColumn = hideEditAnalyticsBtn && hideDeleteAnalyticsBtn;
  const columns: ColumnDef<AnalyticsAccountType>[] = [
    {
      accessorKey: 'accountName',
      header: t<string>('HeaderColumn.Account'),
      cell: ({ row }) => {
        const { avatar, accountName, id: brandAccountId, socialMediaAccounts } = row.original;
        const accounts = formatAccounts(socialMediaAccounts);
        const firstActiveAccount = accounts.find((item) => !!item.id);
        const profileUrl = firstActiveAccount
          ? linkOptions({
              to: '/analytics/$brandAccountId/$socialMedia/$socialMediaAccountId/dashboard',
              params: {
                brandAccountId: brandAccountId.toString(),
                socialMedia: firstActiveAccount.socialMedia,
                socialMediaAccountId: firstActiveAccount.id?.toString() as string
              }
            })
          : '';

        return (
          <div className="flex h-20 items-center">
            <AnalyticsProfile {...profileUrl} src={avatar} size="40px" className="rounded-full" />
            <TableCell.Link lines={2} {...profileUrl} label={accountName} className="ml-4" />
          </div>
        );
      }
    },
    {
      size: 32,
      accessorKey: 'edit',
      meta: { align: 'center', hidden: hideEditColumn },
      header: t<string>('HeaderColumn.Edit'),
      cell: ({ row }) => {
        const hasMoreButton = isAdminStaff && (!hideDeleteAnalyticsBtn || !hideEditAnalyticsBtn);
        const analyticsDetailsLink = getAnalyticsDetailsLink(row.original.id);

        return hasMoreButton ? (
          <SpreadButton
            collapseFrom={0}
            options={[
              {
                icon: 'edit',
                label: t('Edit'),
                ...analyticsDetailsLink,
                disabled: hideEditAnalyticsBtn,
                tooltip: { help: t('Button.Edit') }
              },
              {
                icon: 'delete',
                label: t('Delete'),
                hidden: !(isAdminStaff && !hideDeleteAnalyticsBtn),
                onClick: () => onDelete({ id: row.original.id, name: row.original.accountName })
              }
            ]}
            css={{
              margin: '0 auto',
              width: 'max-content',
              '.single-button, .menu-button': { height: '32px', width: '32px' }
            }}
          />
        ) : !hideEditAnalyticsBtn ? (
          <SpreadButton
            options={[
              {
                icon: 'edit',
                label: t('Edit'),
                ...analyticsDetailsLink,
                tooltip: { help: t('Button.Edit') }
              }
            ]}
            css={{
              margin: '0 auto',
              width: 'max-content',
              '.single-button': { height: '32px', width: '32px' }
            }}
          />
        ) : (
          <TableCell.Text />
        );
      }
    },
    {
      accessorKey: 'socialMediaAnalytics',
      header: t<string>('HeaderColumn.Social Media Analytics'),
      cell: ({ row }) => {
        const { id: brandAccountId, socialMediaAccounts } = row.original;
        const accounts = formatAccounts(socialMediaAccounts);

        return (
          <div className="flex gap-2">
            {accounts.map((account, index) => {
              const { id, socialAccountType, socialMediaUrl, needReconnect, socialMedia } = account;

              return (
                <CustomSocialIcon
                  key={`${brandAccountId}-${index}`}
                  active={!!id}
                  target={id ? '_blank' : '_self'}
                  type={socialAccountType}
                  isWarning={needReconnect}
                  {...(id ? { href: socialMediaUrl } : getAnalyticsDetailsLink(brandAccountId))}
                  className="h-7 w-8"
                  help={needReconnect ? t('Need Reconnect') : capitalizeText(socialMedia)}
                  onClick={(e) => {
                    if (needReconnect) {
                      e.preventDefault();

                      onReconnectAccount(account);
                    }
                  }}
                />
              );
            })}
          </div>
        );
      }
    },
    {
      accessorKey: 'advertiser',
      header: t<string>('HeaderColumn.Advertiser'),
      meta: { hidden: !isAdminStaffAgencyTalentAgencyPartner },
      cell: ({ row }) => (
        <TableCell.Link
          to="/advertiser/$id"
          disabled={hideEditAdvertiserBtn}
          label={row.original.advertiser?.name || ''}
          params={{ id: row.original.advertiser?.id.toString() }}
        />
      )
    },
    {
      accessorKey: 'plan',
      header: t<string>('HeaderColumn.Plan'),
      cell: ({ row }) => (
        <TableCell.Text value={t(ANALYTICS_SUBSCRIPTION_TRANSLATION_KEYS[row.original.subscriptionPlan])} />
      )
    },
    {
      accessorKey: 'pic',
      header: t<string>('HeaderColumn.PIC'),
      meta: { hidden: !isAdminStaffAgencyTalentAgencyPartner },
      cell: ({ row }) => <TableCell.Text value={row.original.accountManagers?.map(({ name }) => name)?.join(', ')} />
    },
    {
      accessorKey: 'country',
      header: t<string>('HeaderColumn.Country'),
      meta: { hidden: !isAdminStaffAgencyTalentAgencyPartner },
      cell: ({ row }) => (
        <TableCell.Text value={row.original.advertiser?.country ? tc(row.original.advertiser.country.name) : ''} />
      )
    }
  ];

  return { columns };
};
